/**
 * Launcher home BFF client (spec 015, T063).
 *
 * GET /api/v1/launcher/home — aggregated home-page data in a single round-trip.
 * See: specs/015-chat-widget-polish/contracts/launcher-home.yaml
 *
 * Partial-failure tolerance: `weather` may be null, `liked_beaches` /
 * `tonight_events` may be empty arrays when upstream services error.
 */
import type { Band } from '@shared/types/surftime';

import { api } from './api';

export type ConditionCode = 'clear' | 'cloudy' | 'rain' | 'snow' | 'fog' | 'sunset' | 'night';

export type LocationSource = 'localtea' | 'browser' | 'default';

export interface LauncherLocation {
  label: string;
  source: LocationSource;
  lat: number | null;
  lon: number | null;
  zip: string | null;
}

export interface LauncherWeather {
  temp_f: number;
  condition_code: ConditionCode;
  condition_label: string;
  sub_line: string | null;
  location: LauncherLocation;
}

export interface LauncherUser {
  id: string;
  email: string;
  is_admin: boolean;
}

export interface LauncherBeachCondition {
  wave_height_ft: number | null;
  wind_speed_mph: number | null;
  /**
   * F-04b-class fix (PWA lane found the sibling gap; desktop sweep found
   * this one): was the retired 4-value `'poor'|'fair'|'good'|'epic'`
   * union, before this field was typed as the shared `Band` — which was
   * an 8-value scale at the time of this fix, later collapsed to 4+no_data
   * (band-collapse pass, 021 fix-wave). `/launcher/home`'s `liked_beaches`
   * reuses `FavoritesService.get_favorites_with_conditions`
   * (`backend/src/launcher/service.py::_safe_liked_beaches` ->
   * `backend/src/surftime/favorites_service.py`) — the same engine that
   * emits the real `Band` value, whatever its current width.
   */
  quality_rating: Band | null;
  observed_at: string | null;
}

export interface LauncherLikedBeach {
  id: string;
  slug: string;
  name: string;
  region: string;
  skill_level?: string | null;
  current_conditions: LauncherBeachCondition | null;
}

export interface LauncherTonightEvent {
  id: string;
  title: string;
  venue_name: string;
  categories?: { slug: string; label: string }[];
  start_date: string;
  price_display: string | null;
  distance_miles: number | null;
}

export interface LauncherHomeResponse {
  user: LauncherUser;
  weather: LauncherWeather | null;
  liked_beaches: LauncherLikedBeach[];
  tonight_events: LauncherTonightEvent[];
}

export async function getLauncherHome(): Promise<LauncherHomeResponse> {
  return api.get<LauncherHomeResponse>('/launcher/home');
}
